import api from '../utils/api';

export interface TicketData {
  id: number;
  eventId: number;
  eventTitle: string;
  eventLocation: string;
  eventAddress: string;
  eventPrefecture?: string | null;
  eventCity?: string | null;
  startDate: string | null;
  endDate: string | null;
  startTime?: string | null;
  endTime?: string | null;
  ticketType: string;
  ticketTypeId?: number;
  ticketPrice: number;
  purchaseDate: string;
  orderId: number;
  orderItemId: number;
  qrToken: string;
  orderQrToken: string;
  sequenceNumber: number;
  status: 'valid' | 'redeemed';
  isValid: boolean;
  eventImageUrl: string | null;
}

export interface TicketGroupResponse {
  orderItemId: number;
  orderId: number;
  orderItemQrToken?: string;
  qrToken: string;
  ticketType: {
    id?: number;
    name?: string;
  };
  event: {
    id?: number;
    title?: string;
    venue?: string;
    address?: string;
    prefecture?: string;
    city?: string;
    startDate?: string | null;
    endDate?: string | null;
    startTime?: string | null;
    endTime?: string | null;
    imageUrl?: string | null;
  };
  buyer?: {
    id: number;
    name?: string;
    email?: string;
  } | null;
  tickets: {
    id: number;
    sequenceNumber: number;
    qrToken?: string;
    status: 'valid' | 'redeemed';
    redeemedAt?: string | null;
    orderItemId?: number;
    orderItemQrToken?: string;
  }[];
  counts: {
    total: number;
    valid: number;
    redeemed: number;
  };
}

export interface PurchaseHistoryItem {
  id: number;
  eventId: number;
  eventTitle: string;
  purchaseDate: string;
  totalAmount: number;
  status: 'pending' | 'completed' | 'canceled';
  items: {
    ticketTypeId: number;
    ticketTypeName: string;
    quantity: number;
    unitPrice: number;
    subtotal: number;
  }[];
}

export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

class UserService {
  /**
   * Fetch user's tickets
   * @returns Promise with array of user's tickets
   */
  async getTickets(): Promise<TicketData[]> {
    try {
      return await api.get<TicketData[]>('/user/tickets');
    } catch (error) {
      console.error('Error fetching tickets:', error);
      throw error;
    }
  }

  async getTicketGroup(qrToken: string): Promise<TicketGroupResponse> {
    try {
      return await api.get<TicketGroupResponse>(`/user/tickets/groups/${qrToken}`);
    } catch (error) {
      console.error('Error fetching ticket group:', error);
      throw error;
    }
  }

  /**
   * Fetch user's purchase history
   * @returns Promise with array of purchase history items
   */
  async getPurchaseHistory(): Promise<PurchaseHistoryItem[]> {
    try {
      return await api.get<PurchaseHistoryItem[]>('/user/purchase-history');
    } catch (error) {
      console.error('Error fetching purchase history:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   * @param profileData User profile data to update
   * @returns Promise with update result
   */
  async updateProfile(profileData: ProfileData): Promise<any> {
    try {
      return await api.put('/user/update', {
        name: {
          firstName: profileData.firstName,
          lastName: profileData.lastName,
          displayName: `${profileData.lastName} ${profileData.firstName}`
        },
        address: {
          // These fields are required by the API but are not exposed in the frontend form
          // In a real app, you might want to make these fields optional or handle them properly
          postal_code: '000-0000',
          prefecture: '未設定',
          city: '未設定',
          street_address: '未設定',
          building_name: null
        }
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
